import { Button } from "@monbits/ui";
import * as React from "react";
import { OTPInput } from "src/components/otp-input/otp-input";

interface TwoFactorVerificationProps {
	onVerify: (code: string) => Promise<boolean>;
	onCancel?: () => void;
	isSubmitting?: boolean;
	showBackupOption?: boolean;
	description?: string;
	submitLabel?: string;
	cancelLabel?: string;
}

export const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({
	onVerify,
	onCancel,
	isSubmitting = false,
	showBackupOption = false,
	description = "Enter the 6-digit code from your authenticator app",
	submitLabel = "Verify",
	cancelLabel = "Cancel",
}) => {
	const [code, setCode] = React.useState("");
	const [useBackupCode, setUseBackupCode] = React.useState(false);
	const [isVerifying, setIsVerifying] = React.useState(false);

	const handleVerify = React.useCallback(async () => {
		const minLength = useBackupCode ? 10 : 6;
		if (code.length !== minLength || isVerifying) return;

		setIsVerifying(true);
		try {
			const success = await onVerify(code);
			if (!success) {
				setCode("");
			}
		} finally {
			setIsVerifying(false);
		}
	}, [code, onVerify, isVerifying, useBackupCode]);

	const handleCodeChange = (value: string) => {
		setCode(value);
	};

	const toggleBackupCode = () => {
		setUseBackupCode(!useBackupCode);
		setCode("");
	};

	return (
		<div className="space-y-6">
			<div>
				<p className="text-sm text-muted-foreground">
					{useBackupCode ? "Enter one of your backup codes." : description}
				</p>
			</div>

			<form
				onSubmit={(e) => {
					e.preventDefault();
					handleVerify();
				}}
				className="space-y-6"
			>
				<OTPInput
					value={code}
					onChange={handleCodeChange}
					onComplete={handleVerify}
					length={useBackupCode ? 10 : 6}
					disabled={isSubmitting || isVerifying}
				/>

				{showBackupOption && (
					<div className="flex justify-center">
						<Button
							type="button"
							variant="link"
							onClick={toggleBackupCode}
							disabled={isSubmitting}
							className="text-sm h-auto p-0"
						>
							{useBackupCode ? "Use authenticator app" : "Use backup code"}
						</Button>
					</div>
				)}

				<div className="flex gap-3">
					<Button
						type="submit"
						disabled={isSubmitting || isVerifying}
						className="flex-1"
					>
						{isSubmitting || isVerifying ? "Verifying…" : submitLabel}
					</Button>

					{onCancel && (
						<Button
							type="button"
							variant="outline"
							onClick={onCancel}
							disabled={isSubmitting || isVerifying}
							className="flex-1"
						>
							{cancelLabel}
						</Button>
					)}
				</div>
			</form>
		</div>
	);
};
